import { useState, useRef, useEffect } from "react";
import { DotsVerticalIcon, ExternalLinkIcon, RefreshIcon, TrashIcon, ClipboardCopyIcon, BanIcon, PlayIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import moment from "moment";
import HeroIcon from "./HeroIcon";
import { Workflow, WorkflowAutomationItem } from "../interfaces";
import { getItemTitle } from "../utils";
import CopyToClipboard from "react-copy-to-clipboard";

interface Props {
  currentItem: WorkflowAutomationItem;
  workflow: Workflow;
  hubspotPortalId?: string;
  itemId?: string;
  dateFormat?: string;
  onRerun?: () => void;
  onDelete?: () => void;
  onRescind?: () => void;
  onRefresh?: () => void;
  canRerun?: boolean;
  canDelete?: boolean;
  canRescind?: boolean;
}

export default function ItemDetailSidebarHeader({ currentItem, workflow, hubspotPortalId, itemId, dateFormat, onRerun, onDelete, onRescind, onRefresh, canRerun, canDelete, canRescind }: Props) {
  const { t } = useTranslation();

  const itemTitle = getItemTitle(currentItem);

  // Runner subheadline
  const runnerName = currentItem.initiatingUser?.fullName ?? currentItem.automationUser?.fullName;
  const createdMoment = moment(currentItem.createdAt);
  const daysSinceCreated = moment().diff(createdMoment, "days");
  const timeLabel = daysSinceCreated < 14
    ? createdMoment.fromNow()
    : createdMoment.format(dateFormat || "DD MMMM YYYY");

  // HubSpot link
  const hubspotUrl = workflow.source?.sourceType === "HUBSPOT" && hubspotPortalId
    ? `https://app.hubspot.com/contacts/${hubspotPortalId}/record/${workflow.source.hubspotConfig?.hubspotObjectTypeId}/${currentItem.sourceItemId}/`
    : undefined;

  // Options menu
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const menuItemClass = "flex items-center font-medium text-xs text-blue w-full px-4 py-2 hover:bg-blue-50 cursor-pointer whitespace-nowrap";

  return (
    <div className="flex items-center justify-between flex-shrink-0 h-16 -mx-4 -mt-4 px-4 border-b border-gray-200">
      <div className="flex items-center gap-2 min-w-0">
        <div
          className="flex items-center justify-center w-9 h-9 text-white rounded flex-shrink-0"
          style={{ backgroundColor: workflow.color }}
        >
          <HeroIcon icon={workflow.icon} className="w-5 h-5" />
        </div>
        <div className="flex flex-col min-w-0">
          <span className="font-gilroy font-semibold text-lg text-black truncate leading-tight">
            {itemTitle}
          </span>
          {runnerName && (
            <span className="text-xs text-gray-500 truncate">
              {t("Ran by")} <span className="font-semibold">{runnerName}</span> · {timeLabel}
            </span>
          )}
        </div>
      </div>
      <div ref={menuRef} className="relative flex-shrink-0 ml-2">
        <button
          type="button"
          onClick={() => setMenuOpen(prev => !prev)}
          className={`flex items-center justify-center p-1 rounded border ${menuOpen ? "bg-gray-100 border-gray-300" : "bg-[#fbfbfb] border-gray-200 hover:bg-gray-100"}`}
        >
          <DotsVerticalIcon className="w-4 h-4 text-gray-400" />
        </button>
        {menuOpen && (
          <div className="absolute mt-1 right-0 z-50 flex flex-col min-w-[160px] bg-white border border-gray-200 rounded overflow-y-auto">
            {onRefresh && (
              <button type="button" className={menuItemClass} onClick={() => { onRefresh(); setMenuOpen(false); }}>
                <RefreshIcon className="w-4 h-4 mr-2" /> {t("Refresh")}
              </button>
            )}
            {itemId && (
              <CopyToClipboard text={itemId} onCopy={() => setMenuOpen(false)}>
                <button type="button" className={menuItemClass}>
                  <ClipboardCopyIcon className="w-4 h-4 mr-2" /> {t("Copy Support Code")}
                </button>
              </CopyToClipboard>
            )}
            {canRescind && onRescind && (
              <button type="button" className={menuItemClass} onClick={() => { onRescind(); setMenuOpen(false); }}>
                <BanIcon className="w-4 h-4 mr-2" /> {t("Cancel Signature Requests")}
              </button>
            )}
            {canRerun && onRerun && (
              <button type="button" className={menuItemClass} onClick={() => { onRerun(); setMenuOpen(false); }}>
                <PlayIcon className="w-4 h-4 mr-2" /> {t("Rerun")}
              </button>
            )}
            {canDelete && onDelete && (
              <button type="button" className={menuItemClass} onClick={() => { onDelete(); setMenuOpen(false); }}>
                <TrashIcon className="w-4 h-4 mr-2" /> {t("Delete")}
              </button>
            )}
            {hubspotUrl && (
              <a
                href={hubspotUrl}
                target="_blank"
                rel="noreferrer"
                className={menuItemClass}
                onClick={() => setMenuOpen(false)}
              >
                <ExternalLinkIcon className="w-4 h-4 mr-2" /> {t("HubSpot Deal")}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
